/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useRef,
  useState,
  useEffect,
  type ComponentProps,
  type CSSProperties,
  type RefObject,
} from 'react';
import throttle from 'lodash/throttle';
import Picture from 'components/Picture/Picture';
import useAnimation from './animations';
import './DoubleTitle.scss';

const DEFAULT_IMAGE: ComponentProps<typeof Picture> = {};

function useMaxSize(): [RefObject<HTMLHeadingElement | null>, number | null] {
  const ref = useRef<HTMLHeadingElement>(null);
  const [size, setSize] = useState<number | null>(null);

  useEffect(() => {
    if (size === null) {
      const style = window
        .getComputedStyle(ref.current!, null)
        .getPropertyValue('font-size');
      const fontSize = parseFloat(style) / 16;

      setSize(fontSize);
    }
  }, [size]);

  return [ref, size];
}

interface DoubleTitleProps {
  heading: string;
  image?: ComponentProps<typeof Picture>;
  author?: string | null;
  photographer?: string | null;
}

/**
 * This is the DoubleTitle component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function DoubleTitle({
  heading,
  image = DEFAULT_IMAGE,
  author = null,
  photographer = null,
}: DoubleTitleProps) {
  const [ref] = useAnimation();
  const container = useRef<HTMLDivElement>(null);
  const [h1, maxSize] = useMaxSize();
  const [size, setSize] = useState<number | null>(null);

  useEffect(() => {
    const onResize = throttle(() => {
      setSize(null);
    }, 200);

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (maxSize !== null) {
      if (
        container.current!.offsetHeight > window.innerHeight ||
        container.current!.offsetWidth > window.innerWidth
      ) {
        setSize(prev => {
          if (prev === null) {
            return maxSize;
          }

          if (prev - 1 > 2) {
            return prev - 1;
          }

          return prev;
        });
      }
    }
  }, [maxSize, size]);

  function getStyle(): CSSProperties | undefined {
    if (!size) return undefined;

    return {
      fontSize: `${size}em`,
    };
  }

  function renderPhotographer() {
    if (!photographer) return null;

    return (
      <>
        {' // '}
        <span className="photo">{photographer}</span>
      </>
    );
  }

  return (
    <header ref={ref} className={`header double-title`} style={getStyle()}>
      <div ref={container}>
        <div className="title">
          <h1 ref={h1}>
            <span dangerouslySetInnerHTML={{ __html: heading }} />
            <span
              dangerouslySetInnerHTML={{ __html: heading }}
              role="presentation"
            />
          </h1>
          <div className="image">
            <Picture {...image} />
          </div>
        </div>
        <div className="copyright">
          <span className="author">{author}</span>
          {renderPhotographer()}
        </div>
      </div>
    </header>
  );
}

export default DoubleTitle;
